<script setup>
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import JuyangAiAPI from 'dashboard/api/juyangAi';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistantId: { type: Number, required: true },
});

const { t } = useI18n();
const history = ref([]);
const message = ref('');
const isSending = ref(false);
const transcript = ref(null);

const scrollToEnd = async () => {
  await nextTick();
  transcript.value?.scrollTo({
    top: transcript.value.scrollHeight,
    behavior: 'smooth',
  });
};

const errorMessage = error =>
  error?.response?.data?.errors?.join(', ') ||
  error?.response?.data?.error ||
  t('JUYANG_AI.ERROR');

const sendMessage = async () => {
  const content = message.value.trim();
  if (!content || isSending.value) return;

  history.value.push({ role: 'user', content });
  message.value = '';
  isSending.value = true;
  await scrollToEnd();

  try {
    const { data } = await JuyangAiAPI.playground(
      props.assistantId,
      history.value
    );
    history.value.push({
      role: 'assistant',
      content: data.content || data.handoff_reason || data.resolve_reason || '',
      toolExecutions: data.metadata?.tool_executions || [],
      knowledgeEntries: data.metadata?.knowledge_entries || [],
    });
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSending.value = false;
    await scrollToEnd();
  }
};
</script>

<template>
  <section class="flex min-h-[32rem] flex-col gap-4">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('JUYANG_AI.PLAYGROUND.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('JUYANG_AI.PLAYGROUND.DESCRIPTION') }}
        </p>
      </div>
      <Button
        icon="i-lucide-rotate-ccw"
        color="slate"
        variant="ghost"
        size="sm"
        :title="t('JUYANG_AI.PLAYGROUND.CLEAR')"
        :disabled="!history.length || isSending"
        @click="history = []"
      />
    </div>

    <div
      ref="transcript"
      class="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto border-y border-n-weak py-4"
    >
      <div
        v-if="!history.length"
        class="grid flex-1 place-content-center text-sm text-n-slate-11"
      >
        {{ t('JUYANG_AI.PLAYGROUND.EMPTY') }}
      </div>
      <div
        v-for="(item, index) in history"
        :key="`${item.role}-${index}`"
        class="flex"
        :class="item.role === 'user' ? 'justify-end' : 'justify-start'"
      >
        <div
          class="max-w-[80%] rounded-lg px-3 py-2 text-sm"
          :class="
            item.role === 'user'
              ? 'bg-n-brand text-white'
              : 'bg-n-alpha-2 text-n-slate-12'
          "
        >
          <p class="mb-1 text-xs opacity-70">
            {{
              item.role === 'user'
                ? t('JUYANG_AI.PLAYGROUND.CUSTOMER')
                : t('JUYANG_AI.PLAYGROUND.ASSISTANT')
            }}
          </p>
          <p class="m-0 whitespace-pre-wrap">{{ item.content }}</p>
          <div
            v-if="item.knowledgeEntries?.length"
            class="mt-2 border-t border-n-weak pt-2"
          >
            <p class="m-0 text-xs font-medium text-n-slate-11">
              {{ t('JUYANG_AI.PLAYGROUND.MATCHED_KNOWLEDGE') }}
            </p>
            <p class="m-0 mt-1 text-xs text-n-slate-10">
              {{ item.knowledgeEntries.map(entry => entry.title).join('、') }}
            </p>
          </div>
          <div
            v-if="item.toolExecutions?.length"
            class="mt-2 border-t border-n-weak pt-2"
          >
            <p class="m-0 text-xs font-medium text-n-slate-11">
              {{ t('JUYANG_AI.PLAYGROUND.TOOL_EXECUTIONS') }}
            </p>
            <ul class="m-0 mt-1 flex list-none flex-col gap-1 p-0">
              <li
                v-for="(execution, executionIndex) in item.toolExecutions"
                :key="executionIndex"
                class="text-xs text-n-slate-10"
              >
                <span
                  :class="
                    execution.status === 'failed'
                      ? 'text-n-ruby-10'
                      : 'text-n-teal-10'
                  "
                >
                  {{ execution.name }} ·
                  {{
                    execution.status === 'failed'
                      ? t('JUYANG_AI.PLAYGROUND.TOOL_FAILED')
                      : t('JUYANG_AI.PLAYGROUND.TOOL_COMPLETED')
                  }}
                </span>
                <p class="m-0 mt-0.5 whitespace-pre-wrap break-words">
                  {{ execution.summary }}
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <p v-if="isSending" class="m-0 text-sm text-n-slate-10">
        {{ t('JUYANG_AI.LOADING') }}
      </p>
    </div>

    <form class="flex items-end gap-2" @submit.prevent="sendMessage">
      <textarea
        v-model="message"
        class="reset-base min-h-20 flex-1 resize-none rounded-lg border-0 bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak focus:outline-n-brand"
        :placeholder="t('JUYANG_AI.PLAYGROUND.PLACEHOLDER')"
        @keydown.enter.exact.prevent="sendMessage"
      />
      <Button
        type="submit"
        icon="i-lucide-send"
        :label="t('JUYANG_AI.PLAYGROUND.SEND')"
        :is-loading="isSending"
        :disabled="!message.trim() || isSending"
      />
    </form>
  </section>
</template>
